import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeHighlightChapterKey } from '../utils/annotationKeys';

const chapterKeyFor = ({ version, book, chapter }) => makeHighlightChapterKey(version, book, chapter);

export const addHighlight = createAsyncThunk('highlights/addHighlight', async ({ chapterKey, newHighlight }, { rejectWithValue }) => {
  try {
    const payload = {
      ...newHighlight,
      version: newHighlight.version || 'ESV',
      chapter: Number(newHighlight.chapter),
      verseId: Number(newHighlight.verseId),
      startOffset: Number(newHighlight.startOffset),
      endOffset: Number(newHighlight.endOffset),
      scope: newHighlight.scope || 'text',
    };
    const response = await fetch('/api/highlight/add', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      throw new Error(errorPayload?.error || `Failed to add highlight (${response.status})`);
    }
    return { chapterKey: chapterKey || chapterKeyFor(newHighlight), highlight: await response.json() };
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchUserHighlights = createAsyncThunk('highlights/fetchUserHighlights', async (_, { rejectWithValue }) => {
  try {
    const response = await fetch('/api/highlight/user');
    if (!response.ok) throw new Error('Failed to fetch user highlights');
    return await response.json();
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchHighlightsByChapter = createAsyncThunk('highlights/fetchHighlightsByChapter', async ({ book, chapter, version }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ book, chapter });
    if (version) params.set('version', version);
    const response = await fetch(`/api/highlight/chapter?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch highlights for the chapter');
    return { chapterKey: chapterKeyFor({ version, book, chapter }), highlights: await response.json() };
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchHighlightsByVerse = createAsyncThunk('highlights/fetchHighlightsByVerse', async ({ book, chapter, verseId, version }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ book, chapter, verseId });
    if (version) params.set('version', version);
    const response = await fetch(`/api/highlight/verse?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch highlights for the verse');
    return { chapterKey: chapterKeyFor({ version, book, chapter }), verseId, highlights: await response.json() };
  } catch (error) { return rejectWithValue(error.message); }
});

export const addHighlightSegment = createAsyncThunk('highlights/addHighlightSegment', async ({ groupId, segment }, { rejectWithValue }) => {
  try {
    const response = await fetch(`/api/highlight/groups/${groupId}/segments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(segment),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.error || `Failed to map highlight (${response.status})`);
    return payload;
  } catch (error) { return rejectWithValue(error.message); }
});

export const deleteHighlight = createAsyncThunk('highlights/deleteHighlight', async (value, { rejectWithValue }) => {
  const highlightId = typeof value === 'object' ? value.highlightId : value;
  try {
    const response = await fetch(`/api/highlight/${highlightId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete highlight');
    return { highlightId, ...(typeof value === 'object' ? { chapterKey: value.chapterKey } : {}), ...(await response.json()) };
  } catch (error) { return rejectWithValue(error.message); }
});

const initialState = { highlights: {}, userHighlights: [], loading: false, loadingByChapter: {}, error: null };

const highlightSlice = createSlice({
  name: 'highlights', initialState, reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addHighlight.fulfilled, (state, action) => {
        const { chapterKey, highlight } = action.payload;
        state.highlights[chapterKey] = [...(state.highlights[chapterKey] || []).filter((item) => item.id !== highlight.id), highlight];
        state.userHighlights = [...state.userHighlights.filter((item) => item.id !== highlight.id), highlight];
      })
      .addCase(addHighlight.rejected, (state, action) => { state.error = action.payload; })
      .addCase(fetchUserHighlights.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserHighlights.fulfilled, (state, action) => { state.loading = false; state.userHighlights = action.payload; })
      .addCase(fetchUserHighlights.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchHighlightsByChapter.pending, (state, action) => {
        const { version, book, chapter } = action.meta.arg;
        state.loadingByChapter[chapterKeyFor({ version, book, chapter })] = true;
      })
      .addCase(fetchHighlightsByChapter.fulfilled, (state, action) => {
        state.highlights[action.payload.chapterKey] = action.payload.highlights;
        state.loadingByChapter[action.payload.chapterKey] = false;
      })
      .addCase(fetchHighlightsByChapter.rejected, (state, action) => { state.error = action.payload; })
      .addCase(fetchHighlightsByVerse.fulfilled, (state, action) => {
        const existing = state.highlights[action.payload.chapterKey] || [];
        state.highlights[action.payload.chapterKey] = [
          ...existing.filter((item) => Number(item.verse_id) !== Number(action.payload.verseId)),
          ...action.payload.highlights,
        ];
      })
      .addCase(deleteHighlight.fulfilled, (state, action) => {
        const { highlightId, chapterKey } = action.payload;
        state.userHighlights = state.userHighlights.filter((item) => item.id !== highlightId);
        if (chapterKey && state.highlights[chapterKey]) {
          state.highlights[chapterKey] = state.highlights[chapterKey].filter((item) => item.id !== highlightId);
        } else {
          Object.keys(state.highlights).forEach((key) => {
            state.highlights[key] = state.highlights[key].filter((item) => item.id !== highlightId);
          });
        }
      })
      .addCase(deleteHighlight.rejected, (state, action) => { state.error = action.payload; });
  },
});

export default highlightSlice.reducer;
